import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  KpiDynamicChartDto,
  KpiChartDataDto,
  KpiChartDisplayType,
  KpiChartType,
  KpiBaseChartDto,
} from '../../../../services/chart/kpi-chart.service';
import { ChartUtilService } from '../../services/chart-util.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  ApexAxisChartSeries,
  ApexNonAxisChartSeries,
  ApexChart,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexTooltip,
  ApexLegend,
} from 'ng-apexcharts';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  title?: ApexTitleSubtitle;
  colors?: string[];
  xaxis?: ApexXAxis;
  labels?: string[];
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
  legend?: ApexLegend;
};

@Component({
  selector: 'chart-renderer',
  standalone: false,
  styleUrls: ['chart-renderer.component.scss'],
  template: `
    <mat-card appearance="outlined" class="card my-card-flex">
      <mat-card-header>
        <mat-card-title-group>
          <mat-card-title>
            <div *ngIf="title" class="mb-4">
              {{ title | translate }}
            </div>
            <strong>
              <span>{{ chart?.title | translate }}</span>
              <span class="time-display-unit" *ngIf="isTimeBasedChart() && chart?.timeDisplayUnit">
                - in
                {{
                  'portal.charts.designer.valueLabels.timeDisplay.' + chart?.timeDisplayUnit + '.label' | translate
                }}</span
              >
            </strong>
            <gem-help-icon *ngIf="helpTooltip" class="ms-1" [tooltip]="helpTooltip | translate"></gem-help-icon>
          </mat-card-title>
          <mat-card-subtitle *ngIf="!isStaticChart()" class="mt-2">
            <div>
              <h5>
                <span *ngIf="process">{{ process | translate }} / </span>
                <span>{{ chartType | translate }}</span>
                <span *ngIf="groupBy"> / {{ groupBy | translate }}</span>
                <span *ngIf="timePreset"> / {{ timePreset | translate }}</span>
              </h5>
            </div>
            <div *ngIf="filterBy">
              <h5>
                {{ filterBy | translate }}: <span> {{ filterValue }}</span>
              </h5>
            </div>
          </mat-card-subtitle>
        </mat-card-title-group>
        <span
          *ngIf="showBookmarked && chart?.showOnDashboard"
          class="material-icons me-1"
          style="color: var(--gematik-neon)"
        >
          dashboard
        </span>
        <span
          *ngIf="showViewCodeButton"
          class="material-icons"
          style="cursor: pointer; color: var(--gematik-blue)"
          [ngbTooltip]="'portal.charts.myCharts.tooltips.viewCode' | translate"
          (click)="onViewCode()"
        >
          code
        </span>

        <span
          *ngIf="showMaximizeButton"
          class="material-icons ms-1"
          style="cursor: pointer; color: var(--gematik-blue)"
          [ngbTooltip]="'portal.charts.myCharts.tooltips.maximize' | translate"
          (click)="onMaximize()"
        >
          all_out
        </span>

        <span
          *ngIf="showEditButton"
          class="material-icons ms-1"
          style="cursor: pointer; color: var(--gematik-blue)"
          [ngbTooltip]="'portal.charts.myCharts.tooltips.edit' | translate"
          (click)="onEdit()"
        >
          edit
        </span>
        <span
          *ngIf="showDeleteButton"
          class="material-icons ms-1"
          style="cursor: pointer; color: var(--gematik-error)"
          [ngbTooltip]="'portal.charts.myCharts.tooltips.delete' | translate"
          (click)="onDelete()"
        >
          delete
        </span>
      </mat-card-header>
      <mat-card-content>
        <div *ngIf="!chart">{{ 'portal.charts.renderer.noData' | translate }}</div>
        @if (isNumberChart()) {
          <chart-renderer-number [chart]="chart"></chart-renderer-number>
        } @else {
          <apx-chart
            *ngIf="chartOptions && !isStaticChart()"
            [series]="chartOptions.series"
            [chart]="chartOptions.chart"
            [xaxis]="chartOptions.xaxis"
            [labels]="chartOptions.labels"
            [dataLabels]="chartOptions.dataLabels"
            [colors]="chartOptions.colors"
            [plotOptions]="chartOptions.plotOptions"
            [tooltip]="chartOptions.tooltip"
            [legend]="chartOptions.legend"
          ></apx-chart>
          <div *ngIf="isStaticChart()">
            <div *ngIf="isNumberOfMyServicesUsersSummaryChart()">
              <users-summary-chart [chart]="chart"></users-summary-chart>
            </div>
            <div *ngIf="isNumberOfMyServicesUsersGrowthChart()">
              <users-per-frequency-chart
                [chart]="chart"
                [frequency]="frequency"
                (frequencyChange)="onFrequencyChange($event)"
              ></users-per-frequency-chart>
            </div>
          </div>
        }
      </mat-card-content>
      <div *ngIf="chart?.lastExecuted" class="bottom-right-text">
        ({{ 'portal.charts.renderer.lastExecuted' | translate }}
        {{ chart?.lastExecuted | date: 'dd.MM.yyyy - HH:mm:ss' }})
      </div>
    </mat-card>
  `,
})
export class ChartRendererComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string;
  @Input() helpTooltip: string;
  @Input() chart!: KpiBaseChartDto;
  @Input() showViewCodeButton: boolean = false;
  @Input() showMaximizeButton: boolean = true;
  @Input() showEditButton: boolean = false;
  @Input() showDeleteButton: boolean = false;
  @Input() showBookmarked: boolean = true;
  @Input() frequency: string;
  @Output() viewCode: EventEmitter<string> = new EventEmitter();
  @Output() maximize: EventEmitter<string> = new EventEmitter();
  @Output() edit: EventEmitter<string> = new EventEmitter();
  @Output() delete: EventEmitter<{ id: string; endpointUrl: string }> = new EventEmitter();
  @Output() frequencyChange: EventEmitter<string> = new EventEmitter();

  public chartOptions?: Partial<ChartOptions>;

  subscriptions: Subscription[] = [];

  constructor(
    private chartUtilService: ChartUtilService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildChart();
    const sub = this.translateService.onLangChange.subscribe(() => {
      this.buildChart();
    });
    this.subscriptions.push(sub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chart'] && this.chart) {
      this.buildChart();
    }
  }

  onViewCode(): void {
    this.viewCode.emit(this.chart.id);
  }

  onMaximize(): void {
    this.maximize.emit(this.chart.id);
  }

  onEdit(): void {
    this.edit.emit(this.chart.id);
  }

  onDelete(): void {
    this.delete.emit({ id: this.chart.id, endpointUrl: this.chart.endpointUrl });
  }

  isStaticChart(): boolean {
    return (
      this.chart?.chartType === KpiChartType.NumberOfMyServicesUsersSummary ||
      this.chart?.chartType === KpiChartType.NumberOfMyServicesUsersGrowth
    );
  }

  isNumberChart(): boolean {
    return this.chart?.displayType === KpiChartDisplayType.number;
  }

  isNumberOfMyServicesUsersSummaryChart(): boolean {
    return this.chart?.chartType === KpiChartType.NumberOfMyServicesUsersSummary;
  }

  isNumberOfMyServicesUsersGrowthChart(): boolean {
    return this.chart?.chartType === KpiChartType.NumberOfMyServicesUsersGrowth;
  }

  onFrequencyChange(e): void {
    this.frequencyChange.emit(e);
  }

  isTimeBasedChart(): boolean {
    return this.chartUtilService.isTimeBasedChart(this.chart?.chartType);
  }

  private buildChart(): void {
    if (!this.isStaticChart()) {
      const chart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
      const { displayType, data, title, color } = chart;

      let labels =
        data?.map((d: KpiChartDataDto) =>
          d.label
            .split('/')
            .filter(Boolean)
            .map(part => this.translateService.instant(part))
            .join(''),
        ) ?? [];

      const values = data ? data?.map((d: KpiChartDataDto) => d?.value) : [];
      // crop labels for pie charts
      if (displayType === KpiChartDisplayType.pie && this.showMaximizeButton) {
        labels = labels.map(label => this.cropLabel(label, 60));
      }
      switch (displayType) {
        case KpiChartDisplayType.pie:
          const pieLabels = labels.map((label, i) =>
            data[i]?.processKey
              ? `${label} (${this.translateService.instant(data[i].processKey)})`
              : label,
          );
          this.chartOptions = {
            series: values,
            chart: {
              type: 'pie',
              height: this.showMaximizeButton ? 250 : 400,
              toolbar: {
                show: true,
                tools: {
                  download: true,
                  selection: false,
                  zoom: false,
                  zoomin: false,
                  zoomout: false,
                  pan: false,
                  reset: false,
                },
              },
            },
            colors: [...this.chartUtilService.getPaletteColors()],
            labels: pieLabels,
            dataLabels: {
              enabled: true,
              formatter: (val: number, opts?: any) => {
                return opts.w.config.series[opts.seriesIndex];
              },
            },
            xaxis: {
              labels: {
                minHeight: this.showMaximizeButton ? 10 : 250,
                maxHeight: this.showMaximizeButton ? 10 : 250,
              },
            },
            title: {
              text: title,
            },
          };
          break;
        case KpiChartDisplayType.bar:
        case KpiChartDisplayType.line:
        default:
          const hasProcessKey = data?.some((d: KpiChartDataDto) => d.processKey);
          let series: ApexAxisChartSeries;
          let chartColors: string[];

          if (hasProcessKey) {
            const uniqueLabels = [...new Set(labels)];
            const processKeys = [...new Set(data.map((d: KpiChartDataDto) => d.processKey))];
            series = processKeys.map(pk => ({
              name: this.translateService.instant(`${pk}`),
              data: uniqueLabels.map(label => {
                const point = data.find((d, i) => labels[i] === label && d.processKey === pk);
                return point?.value ?? 0;
              }),
            }));
            labels = uniqueLabels;
            chartColors = [...this.chartUtilService.getPaletteColors()];
          } else {
            series = [
              {
                name: this.chartType ? this.translateService.instant(this.chartType) : 'Series 1',
                data: values,
              },
            ];
            chartColors = [color ? color : this.chartUtilService.getDefaultColor()];
          }

          this.chartOptions = {
            series,
            chart: {
              type: displayType === KpiChartDisplayType.bar ? 'bar' : 'line',
              height: this.showMaximizeButton ? 250 : 400,
              toolbar: {
                tools: {
                  download: true,
                  selection: false,
                  zoom: false,
                  zoomin: false,
                  zoomout: false,
                  pan: false,
                  reset: false,
                },
              },
            },
            colors: chartColors,
            xaxis: {
              categories: labels,
              labels: {
                minHeight: this.showMaximizeButton ? 150 : 250,
              },
            },
            title: {
              text: title,
            },
            dataLabels: {
              offsetY: -20,
              style: {
                colors: ['var(--gematik-blue)'],
              },
            },
            plotOptions: {
              bar: {
                dataLabels: {
                  position: 'top',
                },
              },
            },
            tooltip: {
              followCursor: true,
              intersect: false,
              shared: false,
            },
            legend: {
              show: hasProcessKey,
              showForSingleSeries: true,
            },
          };
          break;
      }
    }
  }

  private cropLabel(label: string, max = 10): string {
    return label.length > max ? label.slice(0, max) + '...' : label;
  }

  get chartType(): string {
    if (this.chart && this.chart.chartType) {
      return `portal.charts.designer.valueLabels.chartType.${this.chart?.chartType}.label`;
    }
    return null;
  }

  get process(): string {
    if (this.chartUtilService.isStaticChart(this.chart)) {
      return null;
    }
    const dynamicChart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
    if (!dynamicChart.process) {
      return null;
    }
    return `portal.charts.designer.valueLabels.process.${dynamicChart.process}`;
  }

  get groupBy(): string {
    if (this.chartUtilService.isStaticChart(this.chart)) {
      return null;
    }
    const dynamicChart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
    if (!dynamicChart.groupBy) {
      return null;
    }
    return `portal.charts.designer.valueLabels.groupBy.${dynamicChart.groupBy}.label`;
  }

  get timePreset(): string {
    if (this.chartUtilService.isStaticChart(this.chart)) {
      return null;
    }
    const dynamicChart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
    if (!dynamicChart.timePreset) {
      return null;
    }
    return `portal.charts.designer.valueLabels.timePreset.${dynamicChart.timePreset}.label`;
  }

  get filterBy(): string {
    if (this.chartUtilService.isStaticChart(this.chart)) {
      return null;
    }
    const dynamicChart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
    if (!dynamicChart.filterBy) {
      return null;
    }
    return `portal.charts.designer.valueLabels.filterBy.${dynamicChart.filterBy}.label`;
  }

  get filterValue(): string {
    if (this.chartUtilService.isStaticChart(this.chart)) {
      return null;
    }
    const dynamicChart: KpiDynamicChartDto = Object.assign(new KpiDynamicChartDto(), this.chart);
    if (!dynamicChart.filterValue) {
      return null;
    }
    return `${dynamicChart.filterValueDisplay}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
